"use client";

import { Input, Textarea } from "../components/input";
import { FieldFrame } from "./fields";
import type { LocalizedName } from "./localized";
import { useText } from "./text";

/** A text being edited in both languages; blank Chinese falls back to English once saved. */
export interface LocalizedDraft {
  readonly en: string;
  readonly zh: string;
}

export const draftOf = (text: LocalizedName | null | undefined): LocalizedDraft => ({
  en: text?.en ?? "",
  zh: text?.zh ?? "",
});

export const isBlankDraft = (draft: LocalizedDraft) => draft.en.trim() === "" && draft.zh.trim() === "";

/** The draft as the API takes it: trimmed, with Chinese left out when blank. */
export function textOf(draft: LocalizedDraft): { en: string; zh?: string } {
  const en = draft.en.trim();
  const zh = draft.zh.trim();
  return zh === "" ? { en } : { en, zh };
}

/** One text in English, required, and Chinese, optional, with errors already translated. */
export function LocalizedFields({
  id,
  label,
  value,
  onChange,
  maxLength,
  multiline = false,
  errors = {},
}: {
  id: string;
  label: string;
  value: LocalizedDraft;
  onChange: (value: LocalizedDraft) => void;
  maxLength: number;
  multiline?: boolean;
  errors?: { en?: string | undefined; zh?: string | undefined };
}) {
  const { t } = useText();
  // One line each sits side by side from the small breakpoint; descriptions stack, each the full width.
  return (
    <div className={multiline ? "flex flex-col gap-4" : "grid gap-4 sm:grid-cols-2"}>
      {(["en", "zh"] as const).map((lang) => {
        const fieldId = `${id}-${lang}`;
        const error = errors[lang];
        const props = {
          id: fieldId,
          value: value[lang],
          maxLength,
          "aria-invalid": error !== undefined,
          "aria-describedby": error === undefined ? undefined : `${fieldId}-error`,
          onChange: (event: { target: { value: string } }) =>
            onChange({ ...value, [lang]: event.target.value }),
        };
        return (
          <FieldFrame key={lang} id={fieldId} label={t(`localized.${lang}`, { label })} error={error}>
            {multiline ? <Textarea rows={4} {...props} /> : <Input {...props} />}
          </FieldFrame>
        );
      })}
    </div>
  );
}
